import moment from "moment";
import { HistoricCECTypeString } from "./constantsString";
/**
 * Table Data Formatters - Utility functions for formatting table data
 * All formatters are robust to missing/null/invalid data.
 */
export const pressureFormatter = (params) => {
  const hasSuperscriptChar = (value, char) => {
    return typeof value === "string" && value.includes(char);
  };
  const hasSuperscriptA = hasSuperscriptChar(params.value, "ᵃ");
  const hasSuperscriptB = hasSuperscriptChar(params.value, "ᵇ");

  const numericValue =
    hasSuperscriptA || hasSuperscriptB
      ? parseFloat(params.value.replace("ᵃ", "").replace("ᵇ", "").trim())
      : parseFloat(params.value);

  if (isNaN(numericValue)) {
    return "*";
  } else if (numericValue < 0) {
    return hasSuperscriptA ? "< 0 ᵃ" : hasSuperscriptB ? "< 0 ᵇ" : "< 0";
  } else if (numericValue > 100) {
    return hasSuperscriptA ? "> 100 ᵃ" : hasSuperscriptB ? "> 100 ᵇ" : "> 100";
  } else {
    return (Math.floor(numericValue * 10) / 10).toFixed(1);
  }
};
export const multipleReasonFormatter = (type) => {
  if (typeof type === "string") {
    type = type.split(",");
  }
  if (Array.isArray(type)) {
    const formattedType = type.map((item) => {
      const trimmedItem = item.trim();
      return trimmedItem;
    });
    const resultString = formattedType.join(", ");
    return resultString;
  }
  return type;
};

export const dateFormatter = (params) => {
  if (!params || !params.value) return "-";
  return moment(params.value).isValid()
    ? moment(params.value).format("DD/MM/YYYY")
    : "-";
};
export const dateTimeFormatter = (params) => {
  if (!params || !params.value) return "-";
  return moment(params.value).isValid()
    ? moment(params.value).format("DD/MM/YYYY HH:mm:ss")
    : "-";
};

export const timeFormatter = (params) => {
  // Convert UTC to IST (Indian Time, UTC+5:30)
  if (!params.value) return "";
  const utcDate = new Date(params.value);
  // Get IST offset in minutes (5 hours 30 minutes = 330 minutes)
  const istOffset = 330;
  const istDate = new Date(utcDate.getTime() + istOffset * 60000);
  // Format as needed, e.g., YYYY-MM-DD HH:mm:ss
  return istDate.toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  });
};

export const typeFormatter = (params, t) => {
  if (!params.value) {
    return "";
  }
  return params.value?.toLowerCase() === "c" ||
    params.value?.toLowerCase() === "cip"
    ? t("foulingStrings.cip")
    : params.value?.toLowerCase() === "ceb"
      ? t("foulingStrings.ceb")
      : t("foulingStrings.shutdown");
};
export const valueFormatter = (params, cectype) => {
  const type = params?.data?.[cectype]?.toString().trim().toLowerCase();
  return type === "cip" || type === "ceb" || type === "c" ? params.value : "-";
};
export const soakDurationFormatter = (params, cectype) => {
  const type = params?.data?.[cectype]?.toString().trim().toLowerCase();
  if (type === "cip" || type === "c") {
    return params.value + " hours";
  } else if (type === "ceb") {
    return params.value + " minutes";
  } else {
    return "-";
  }
};
export const predictTypeFormatter = (params, t) => {
  if (params && params.value) {
    const val = params.value.toLowerCase();
    return HistoricCECTypeString[val]
      ? t(HistoricCECTypeString[val])
      : params.value;
  }
  return "-";
};

export const sourceFormatter = (params, t) => {
  if (!params.data || Object.keys(params.data).length === 0) return "";
  if (params.data.is_tool_identified === true) {
    return t("IdentifiedCIPStrings.calculation");
  } else if (params.data.is_tool_identified === false) {
    return t("IdentifiedCIPStrings.user");
  } else {
    return "";
  }
};
export const averageFormatter = (params, avgField, sdField) => {
  const avg = parseFloat(params.data[avgField]);
  const sd = parseFloat(params.data[sdField]);
  if (avg || sd) {
    return `${avg.toFixed(2)} (± ${sd.toFixed(2)})`;
  }
  return `-`;
};
export const averageEffectivenessFormatter = (params, avgField, sdField) => {
  const avg = parseFloat(params.data[avgField]);
  const sd = parseFloat(params.data[sdField]);
  if (avg || sd) {
    let result = `${avg.toFixed(2)} (± ${sd.toFixed(2)})`;
    if (avg > 100) {
      result += " ᵃ";
    } else if (avg < 0) {
      result += " ᵇ";
    }
    return result;
  }
  return `-`;
};

export const cleaningFormatter = (params) => {
  if (params.value) {
    return params.value.toFixed(2);
  }
  return `-`;
};
export const cleaningEfficitivenessFormatter = (params) => {
  if (
    params.value === null ||
    params.value === undefined ||
    isNaN(params.value)
  ) {
    return "*";
  } else if (params.value < 0) {
    return "< 0% ᵇ";
  } else if (params.value > 100) {
    return "> 100% ᵃ";
  } else {
    return params.value.toFixed(2);
  }
};

function splitLastOccurrence(str, substring) {
  if (str) {
    const lastIndex = str.lastIndexOf(substring);
    const before = str.slice(0, lastIndex);
    return before;
  } else {
    return str;
  }
}

export const dateComparator = (date1, date2) => {
  const date1Updated = splitLastOccurrence(date1, " ");
  const date2Updated = splitLastOccurrence(date2, " ");
  const d1 = new Date(date1Updated);
  const d2 = new Date(date2Updated);
  if (d1 > d2) {
    return -1;
  } else if (d1 < d2) {
    return 1;
  } else {
    return 0;
  }
};

export const getSortedData = (unsortedData) => {
  const data = [...unsortedData].sort((a, b) => {
    if (!a.cleaning_start_time && !b.cleaning_start_time) {
      return 0;
    } else if (!a.cleaning_start_time) {
      return 1;
    } else if (!b.cleaning_start_time) {
      return -1;
    } else {
      return new Date(a.cleaning_start_time) - new Date(b.cleaning_start_time);
    }
  });

  //console.log("Data is ", data);

  return data;
};

export const formattedCurrencyValue = (value, currency) => {
  let formattedValue = value;
  const languageCurrency = getLanguageCode[currency].currency;
  const currencySymbol = getLanguageCode[currency].symbol;
  const currencyCode = getLanguageCode[currency].code;

  if (value) {
    formattedValue = value.toLocaleString(currencyCode, {
      style: "currency",
      currency: languageCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (currencyCode === "zh-CN") {
      if (value > 0) {
        const currencySymbol = formattedValue.slice(0, 1);
        formattedValue = formattedValue.slice(1) + currencySymbol;
      } else {
        const currencySymbol = formattedValue.slice(1, 2);
        formattedValue =
          formattedValue.slice(0, 1) + formattedValue.slice(2) + currencySymbol;
      }
    }
  } else {
    if (currencyCode === "zh-CN") {
      return `${formattedValue}${currencySymbol}`;
    } else {
      return `${currencySymbol}${formattedValue}`;
    }
  }
  if (currencyCode === "zh-CN" && formattedValue)
    formattedValue = formattedValue.replace("¥", "元");

  return formattedValue === 0
    ? `${currencySymbol}${formattedValue}`
    : formattedValue;
};

const getLanguageCode = {
  USD: {
    code: "en-US",
    currency: "USD",
    symbol: "$",
  },
  Yuan: {
    code: "zh-CN",
    currency: "CNY",
    symbol: "元",
  },
  Euro: {
    code: "EUR",
    currency: "EUR",
    symbol: "€",
  },
};
